/**
 * D1 · অফারিং বিস্তার — `/offering/:id`
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, Flow D, frame D1 (360 × 680).
 * Every value below is read off that frame's inline styles. docs/01 § the rule.
 *
 * Frame note: "Price and one action pinned to the bottom bar at every scroll position.
 * The service list carries prices, so the user is never choosing blind."
 *
 * ── WIRED ────────────────────────────────────────────────────────────────────
 * The offering comes from `GET /v1/offerings/:id`. Not one pixel of the healthy render
 * changed: the same strings arrive from the server that used to sit in `data.ts`, and
 * every amount arrives already formatted, so this file no longer calls `formatTaka` —
 * the client formats no money (contracts § MoneyDisplay).
 */
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, IconButton, VerifiedBadge } from '@oprappo/ui';
import type { OfferingResponse } from '@oprappo/contracts';
import { Frame, SAND_FRAME, MANROPE_THEN_ANEK_CLASS } from './chrome.js';
import { Loaded, commerce, useResource } from './commerce.js';

export function D1OfferingView({ offering }: { offering: OfferingResponse }): JSX.Element {
  const navigate = useNavigate();
  const o = offering;

  return (
    <Frame className={SAND_FRAME}>
      {/* ছবির স্থানধারক — h:158, dashed hairline, back control floated top-left */}
      <div className="h-[158px] box-content bg-sand-deep border-b border-dashed border-border-warm flex items-center justify-center relative">
        <div
          className={`font-medium text-[13px] leading-[1.75] text-muted ${MANROPE_THEN_ANEK_CLASS}`}
        >
          ছবির স্থানধারক
        </div>
        <div className="absolute top-[12px] left-[12px] w-[32px] h-[32px] box-content bg-white/[.92] border border-border flex items-center justify-center">
          <IconButton control="back" size={17} onClick={() => navigate(-1)} />
        </div>
      </div>

      <div className="flex-1 p-[16px] oprappo-scroll">
        <div className="flex justify-between items-start gap-[11px]">
          <div className="flex-1">
            <div className="font-anek font-semibold text-[20px] leading-[1.75] text-ink">
              {o.provider_name_bn}
            </div>
            <div className="font-anek font-normal text-[14px] leading-[1.75] text-muted mt-[2px]">
              {o.provider_meta_bn}
            </div>
          </div>
          {/*
            The badge never sits alone — tapping it opens H1, which lists the gaps too.

            AND IT IS NOW CONDITIONAL, which it was not. The label was the literal
            `যাচাইকৃত` written here, so this screen told every customer that every
            provider was verified — including one nobody had checked. The server already
            knew the answer and already withholds the badge on H1; it simply was not
            being asked. It is asked now, and the word comes from the response rather than
            from this file, so there is one place that decides what the badge means.
          */}
          {o.verified_badge_bn === null ? null : (
            <VerifiedBadge
              labelBn={o.verified_badge_bn}
              onExplain={() => navigate(`/provider/${o.provider_id}/verification`)}
            />
          )}
        </div>

        <div className="flex gap-[16px] mt-[13px] py-[12px] border-t border-b border-border font-anek font-medium text-[14px] leading-[1.75]">
          <div className="text-ink">{o.rating_bn}</div>
          <div className="text-muted">{o.jobs_bn}</div>
          <div className="text-muted">{o.eta_bn}</div>
        </div>

        <div className="font-anek font-normal text-[14.5px] leading-[1.75] text-ink2 mt-[14px]">
          {o.about_bn}
        </div>

        <div className="mt-[18px]">
          <div className="font-anek font-semibold text-[16px] leading-[1.75] text-ink mb-[10px]">
            যা করেন
          </div>
          <div>
            {o.services.map((s) => (
              <div
                key={s.key}
                className="py-[11px] border-t border-border flex justify-between items-baseline"
              >
                <div className="font-anek font-normal text-[14.5px] leading-[1.75] text-ink2">
                  {s.label_bn}
                </div>
                <div className="font-manrope font-semibold text-[15px] leading-[1] text-ink tabular-nums">
                  {s.price.display}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border-t border-border shadow-bottombar px-[16px] py-[12px] flex gap-[13px] items-center">
        {/*
         * The visit fee is null for an offering that has none — a grocery product does,
         * and `৳ 0` would be a claim about a price rather than the absence of one. The
         * drawn offering has one, so the drawn screen is unchanged.
         */}
        {o.visit_fee === null ? null : (
          <div className="flex-none">
            <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted">
              {o.visit_fee_label_bn}
            </div>
            <div className="font-manrope font-bold text-[19px] leading-[1.1] text-ink tabular-nums mt-[2px]">
              {o.visit_fee.display}
            </div>
          </div>
        )}
        <div className="flex-1">
          {/*
           * No file states where "বুক করুন" goes. Flow D's own intro says one checkout
           * handles a recharge, a technician booking and a market order, so booking
           * enters that checkout. Reported as a chosen value, not a found one.
           */}
          <Button onClick={() => navigate('/checkout')}>বুক করুন</Button>
        </div>
      </div>
    </Frame>
  );
}

export function D1Offering(): JSX.Element {
  const { id } = useParams();
  const offeringId = id ?? '';
  const load = useCallback(
    (signal: AbortSignal) => commerce.offering(offeringId, signal),
    [offeringId],
  );
  const [resource] = useResource(load, `offering:${offeringId}`);

  return <Loaded resource={resource}>{(offering) => <D1OfferingView offering={offering} />}</Loaded>;
}
